import { Button, Group, Radio, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCircleOff } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { useLoadTemplateV2 } from 'shared/hooks/use-load-template-v2';
import { useUpdateTemplate } from '../api';
import type { TemplateChannel } from './channels';
import { draftKey, useTemplateDraftsAtom } from './state';
import type { StatementType, StatementTypeOption } from './statement-types';

const NO_STATEMENT = 'none';

type StatementTypeModalProps = {
  templateId: string;
  currentValue: StatementType | null;
  options: ReadonlyArray<StatementTypeOption>;
  onClose: () => void;
};

export const StatementTypeModal = ({
  templateId,
  currentValue,
  options,
  onClose,
}: StatementTypeModalProps) => {
  const { t } = useTranslation();
  const { template } = useLoadTemplateV2(templateId);
  const [drafts] = useTemplateDraftsAtom();
  const { updateTemplate, isUpdateTemplateLoading } = useUpdateTemplate();
  const [selected, setSelected] = useState<string>(currentValue ?? NO_STATEMENT);

  const handleConfirm = () => {
    if (!template) return;

    const groupedType = selected === NO_STATEMENT ? null : selected;
    const buildBodies = (channel: TemplateChannel) =>
      template.bodies[channel].map(body => ({
        id: body.id,
        groupedType,
        locale: body.locale,
        body: drafts[draftKey(template.id, channel, body.locale)] ?? body.body,
        subject: body.subject,
        fromName: body.fromName,
        fromEmail: body.fromEmail,
        replyToName: body.replyToName,
        replyToEmail: body.replyToEmail,
      }));

    updateTemplate(
      { id: template.id, bodies: { email: buildBodies('email'), letter: buildBodies('letter') } },
      { onSuccess: onClose },
    );
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {t(i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.MODAL_DESCRIPTION)}
      </Text>

      <Radio.Group value={selected} onChange={setSelected}>
        <Stack gap="sm">
          <Radio.Card value={NO_STATEMENT} p="sm" radius="md">
            <Group gap="sm" wrap="nowrap" align="center">
              <ThemeIcon variant="light" size={40} radius="md">
                <IconCircleOff size={22} stroke={1.5} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" fw={600}>
                  {t(i18nKeys.NO_STATEMENT)}
                </Text>
                <Text size="xs" c="dimmed">
                  {t(i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.NO_STATEMENT_DESCRIPTION)}
                </Text>
              </Stack>
              <Radio.Indicator ml="auto" />
            </Group>
          </Radio.Card>

          <SimpleGrid cols={{ base: 1, sm: Math.min(options.length, 2) }} spacing="sm">
            {options.map(({ value, Icon, titleKey, descriptionKey }) => (
              <Radio.Card key={value} value={value} p="sm" radius="md">
                <Stack gap="xs" h="100%">
                  <Group gap="sm" wrap="nowrap" align="center">
                    <ThemeIcon variant="light" size={40} radius="md">
                      <Icon size={22} stroke={1.5} />
                    </ThemeIcon>
                    <Text size="sm" fw={600}>
                      {t(titleKey)}
                    </Text>
                    <Radio.Indicator ml="auto" />
                  </Group>
                  <Text size="xs" c="dimmed">
                    {t(descriptionKey)}
                  </Text>
                </Stack>
              </Radio.Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Radio.Group>

      <Group justify="flex-end" mt="xs">
        <Button variant="default" onClick={onClose} disabled={isUpdateTemplateLoading}>
          {t(i18nKeys.CANCEL)}
        </Button>
        <Button onClick={handleConfirm} loading={isUpdateTemplateLoading}>
          {t(i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.APPLY)}
        </Button>
      </Group>
    </Stack>
  );
};
